/**
 * GatewayInstruments — the multi-instrument panel for a gateway that polls
 * devices (any product with drivers: a VSync, a Modbus gateway, the
 * Multi-protocol Gateway).
 *
 * Multi-protocol (web brief U6): on a gateway that accepts config schema v2
 * (its birth), "Add instrument" is the v2 form (InstrumentFormV2: protocol,
 * connection from the driver's link_schema, points from its address_schema)
 * and a v2 instrument's connection is edited with PATCH (InstrumentLinkEditor).
 * A gateway without config schema v2 (a VSync box, a Blackbox, GatewayPro
 * <= 0.2), or a server without the drivers registry, keeps the v1 Modbus form
 * below exactly as it was. Each instrument can show its live values with
 * quality (InstrumentValues).
 *
 * A VSync hosts many instruments, each a CHILD sensor Thing (parent_thing_id =
 * the gateway) with its own Modbus register map. This lists them and adds new
 * ones via `POST /v1/things/{gw}/instruments`, prefilling the register rows from
 * the equipment-template catalog (14 real VSync models). It REPLACES the old
 * single-template flow (which flattened one template's tags onto the gateway).
 *
 * data_type here is the Modbus wire type (int16/int32/float32/double), matching
 * createInstrument + the templates — NOT the 8-option tag facade, so this uses
 * its own compact register editor rather than TagRowsEditor.
 *
 * The add form also sets the instrument's link (TCP: IP + port; RTU: serial port
 * + line settings), defaulting the transport from the gateway's product, and a
 * word order on each param whose type spans 2+ registers.
 *
 * Values open by themselves (2026-10-30): every instrument's on a small gateway,
 * the first one's on a big one (lib/iot/gateway-live defaultOpenInstruments).
 * The gateway sheet holds the open set (openValues / onOpenValuesChange) so it
 * stays across its tabs; without those props the panel holds it. Each
 * instrument says how long ago its data last arrived (its last_seen). The
 * list reads every page of instruments (the server's page was 50) and draws
 * them ten at a time, with "Show 10 more" and "Show all".
 */

import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Loader2, Plus, Trash2, X, Cpu, Pencil, Plug, Upload } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import { useToast } from "@/lib/ui/toast";
import { backendErrorMessage, tags as tagsApi, type ThingDataType } from "@/lib/api";
import {
  things,
  parseWordOrder,
  type CreateInstrumentBody,
  type EquipmentTemplate,
  type InstrumentParamInput,
  type ThingTag,
} from "@/lib/api/things";
import {
  TagRowsEditor,
  WordOrderSelect,
  blankRow,
  namedRowsOf,
  toTagInputs,
  type TagRow,
} from "@/components/iot/ThingTagRows";
import { registerMapToRows, type RegisterMap } from "@/components/connect/commit";
import { useGatewayDrivers } from "@/components/connect/gateway-queries";
import { InstrumentFormV2, InstrumentLinkEditor } from "@/components/iot/InstrumentFormV2";
import { InstrumentValues } from "@/components/iot/InstrumentValues";
import { Badge } from "@/lib/ui/badge";
import type { Thing } from "@/lib/api";
import { driverLabel, isModbusDriver } from "@/lib/iot/drivers";
import { hasBirth, readBirth, speaksConfigV2 } from "@/lib/iot/gateway-birth";
import { defaultOpenInstruments, useNow } from "@/lib/iot/gateway-live";
import { readingAge } from "@/lib/iot/instrument-points";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/ui/tooltip";

type ModbusType = "int16" | "int32" | "float32" | "double";
const MODBUS_TYPES: ModbusType[] = ["int16", "int32", "float32", "double"];

/** Registers each wire type occupies: 16-bit = 1, 32-bit = 2, 64-bit = 4. Word order
 *  only applies to a type wider than one register. */
const TYPE_REGISTERS: Record<ModbusType, number> = { int16: 1, int32: 2, float32: 2, double: 4 };

type Transport = CreateInstrumentBody["protocol"];
type Parity = NonNullable<CreateInstrumentBody["parity"]>;

const TRANSPORTS: { value: Transport; label: string }[] = [
  { value: "tcp", label: "TCP" },
  { value: "rtu", label: "RTU (serial)" },
];
/** Standard Modbus RTU line speeds for the Baud picker (9600 is the default). */
const BAUD_RATES = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];
const PARITY_LABEL: Record<Parity, string> = { N: "None (N)", E: "Even (E)", O: "Odd (O)" };
const PARITIES: Parity[] = ["N", "E", "O"];

const SELECT_CLASS =
  "rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface";

const isPort = (n: number) => Number.isInteger(n) && n >= 1 && n <= 65535;

/** Instrument cards drawn at first, and added by each "Show more". */
const INSTRUMENTS_STEP = 10;
/** No instrument open (before the first list). */
const NONE_OPEN: ReadonlySet<string> = new Set();

/** Poll default for hand-added rows in the edit-tags editor. */
const EDIT_POLL_DEFAULT = 5;

/**
 * A `GET /v1/things/{id}/tags` row → an editable {@link TagRow}, preserving the
 * Modbus wire metadata the editor must round-trip: address, register count,
 * register space (a coil stays a coil), scale/offset + poll. Facade is inferred
 * from the register space + data_kind so a coil edits as a boolean, a count as an
 * integer, a percentage as a percentage.
 */
function thingTagToRow(t: ThingTag): TagRow {
  const space = t.register_space ?? undefined;
  const isCoil = space === "coil" || space === "discrete" || t.data_kind === "boolean_state";
  const facade: ThingDataType = isCoil
    ? "boolean"
    : t.data_kind === "count_scalar"
      ? "integer"
      : t.data_kind === "analog_percentage"
        ? "percentage"
        : "number";
  return {
    key: t.id,
    name: t.name,
    data_type: facade,
    unit: t.unit ?? "",
    poll_sec: t.poll_sec != null ? String(t.poll_sec) : "",
    address: t.address ?? "",
    min: t.min_value != null ? String(t.min_value) : "",
    max: t.max_value != null ? String(t.max_value) : "",
    p_id: t.p_id ?? undefined,
    p_type: t.p_type ?? undefined,
    reg_count: t.reg_count ?? undefined,
    is_cycle_counter: t.is_cycle_counter ?? undefined,
    register_kind: space,
    scale: t.scale ?? undefined,
    offset: t.reg_offset ?? undefined,
    data_kind: t.data_kind,
    word_order: parseWordOrder(t.word_order),
  };
}

interface ParamRow extends InstrumentParamInput {
  data_type: ModbusType;
  reg_count: number;
  is_cycle_counter: boolean;
}

const blankParam = (): ParamRow => ({
  metric: "",
  register: "",
  reg_count: 1,
  data_type: "float32",
  is_cycle_counter: false,
});

/**
 * The reg_count to send for a param. Hand-added rows start at 1, but the gateway
 * reads exactly reg_count registers and a float32 / int32 value is two of them (a
 * double four), so a 1 on a wider type is raised to the type's width. Any other
 * count is sent as is (the templates already carry the right one).
 */
const regCountFor = (p: ParamRow): number => {
  const n = p.reg_count || 1;
  return n === 1 ? TYPE_REGISTERS[p.data_type] : n;
};

interface FormState {
  name: string;
  equipmentType: string;
  protocol: Transport;
  slaveId: number;
  /** TCP link: the device's IP (required) + port. */
  ip: string;
  port: number;
  /** RTU link: the host serial device a PC / Pi gateway opens (a VSync box ignores
   *  it) + the line settings. */
  serialPort: string;
  baud: number;
  dataBits: 7 | 8;
  parity: Parity;
  stopBits: 1 | 2;
  pollSec: number;
  params: ParamRow[];
}

const emptyForm = (protocol: Transport): FormState => ({
  name: "",
  equipmentType: "",
  protocol,
  slaveId: 1,
  ip: "",
  port: 502,
  serialPort: "",
  baud: 9600,
  dataBits: 8,
  parity: "N",
  stopBits: 1,
  pollSec: 30,
  params: [blankParam()],
});

export interface GatewayInstrumentsProps {
  gatewayId: string;
  /** The instruments whose values are open, held by the caller; null: the default set. */
  openValues?: Set<string> | null;
  /** Set with openValues: the caller holds the open set. */
  onOpenValuesChange?: (ids: Set<string>) => void;
}

export function GatewayInstruments({ gatewayId, openValues, onOpenValuesChange }: GatewayInstrumentsProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<FormState | null>(null);

  const instrumentsQ = useQuery({
    queryKey: ["things", "instruments", gatewayId],
    queryFn: () => things.listAll({ gateway_id: gatewayId, kind: "sensor" }),
  });
  const templatesQ = useQuery({
    queryKey: ["equipment-templates"],
    queryFn: () => things.equipmentTemplates(),
    staleTime: 60 * 60_000, // reference data — cache for the session
  });
  // Default transport from the gateway's product: a Modbus-RTU product (a VSync,
  // an RTU gateway) starts on RTU; Modbus-TCP (a Blackbox, a TCP gateway) or a
  // product not resolved yet starts on TCP. The other is one click away. Both
  // reads share their cache keys with the detail sheet and the devices page.
  const gatewayQ = useQuery({
    queryKey: ["thing", gatewayId],
    queryFn: () => things.get(gatewayId),
    staleTime: 5 * 60_000,
  });
  const productsQ = useQuery({
    queryKey: ["thing-products"],
    queryFn: () => things.products(),
    staleTime: 5 * 60_000,
  });
  const gatewayProduct = productsQ.data?.products.find((p) => p.id === gatewayQ.data?.product_id);
  const defaultProtocol: Transport = /rtu/i.test(gatewayProduct?.field_protocol ?? "") ? "rtu" : "tcp";

  // Multi-protocol: the v2 forms on a gateway that accepts config schema v2.
  const driversQ = useGatewayDrivers(gatewayId);
  const allDrivers = driversQ.data?.drivers;
  const birth = readBirth(gatewayQ.data);
  const v2Gateway = (allDrivers?.length ?? 0) > 0 && speaksConfigV2(birth);
  const v2Drivers = (allDrivers ?? []).filter((d) => d.available);
  const serialPorts = hasBirth(gatewayQ.data) ? birth.serialPorts : undefined;
  const waitingForBirth =
    !v2Gateway && (allDrivers ?? []).some((d) => !isModbusDriver(d.id) && d.unavailable_reason === "no_birth");
  const [v2Form, setV2Form] = useState(false);
  const [linkEdit, setLinkEdit] = useState<Thing | null>(null);
  const [loadingLinkFor, setLoadingLinkFor] = useState<string | null>(null);
  // The open values: the caller's set, else this panel's; until someone picks, the default set.
  const [ownOpen, setOwnOpen] = useState<Set<string> | null>(null);
  const picked = onOpenValuesChange ? (openValues ?? null) : ownOpen;
  // The default is decided once, on the first list that has instruments (the sheet mounts this panel per gateway):
  // a later add or a re-read must not close a panel nobody closed, or open many when a 4th instrument arrives. On a
  // gateway that had none, the first instrument added opens.
  const [defaultOpen, setDefaultOpen] = useState<Set<string> | null>(null);
  if (defaultOpen === null && instrumentsQ.data && instrumentsQ.data.items.length > 0) {
    setDefaultOpen(defaultOpenInstruments(instrumentsQ.data.items));
  }
  const valuesFor = picked ?? defaultOpen ?? NONE_OPEN;
  const toggleValues = (id: string) => {
    const next = new Set(valuesFor);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (onOpenValuesChange) onOpenValuesChange(next);
    else setOwnOpen(next);
  };
  // Moves each instrument's "data ... ago" on (its last_seen moves at most once a minute).
  const now = useNow(15_000);
  // A gateway with many instruments draws them ten at a time ("Show 10 more"): each card is a row of controls,
  // and an open one polls its values.
  const [shownCount, setShownCount] = useState(INSTRUMENTS_STEP);
  // "Show more" moves the focus to the first card it shows (its Values button), since the button itself may go.
  const valuesButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterShow = useRef<string | null>(null);
  const showMore = (count: number) => {
    focusAfterShow.current = instruments[shownCount]?.id ?? null;
    setShownCount(count);
  };
  useEffect(() => {
    const id = focusAfterShow.current;
    if (!id) return;
    focusAfterShow.current = null;
    valuesButtons.current.get(id)?.focus();
  }, [shownCount]);

  const openLinkEditor = async (id: string) => {
    setForm(null);
    setV2Form(false);
    setLoadingLinkFor(id);
    try {
      setLinkEdit(await things.get(id));
    } catch (e) {
      toast.push("error", backendErrorMessage(e));
    } finally {
      setLoadingLinkFor(null);
    }
  };

  const templates = templatesQ.data?.templates ?? [];
  const instruments = instrumentsQ.data?.items ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["things", "instruments", gatewayId] });

  const createM = useMutation({
    mutationFn: (body: CreateInstrumentBody) => things.createInstrument(gatewayId, body),
    onSuccess: (res) => {
      toast.push(
        "ok",
        res.config_pushed
          ? `Instrument added — config pushed to the device (${res.params} params).`
          : `Instrument added (${res.params} params). It will apply on the device's next config pull.`,
      );
      setForm(null);
      void invalidate();
    },
    onError: (e) => toast.push("error", backendErrorMessage(e)),
  });

  const deleteM = useMutation({
    mutationFn: (childId: string) => things.remove(childId),
    onSuccess: () => {
      toast.push("ok", "Instrument removed.");
      void invalidate();
    },
    onError: (e) => toast.push("error", backendErrorMessage(e)),
  });
  // Removing an instrument drops its tags and stops its polling: ask first.
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);

  // --- Edit an existing instrument's tags (#1 / #2) ------------------------
  // Load the instrument's current tags into the shared row editor, let the
  // operator add / rename / retype / remove tags (with register space + scale,
  // and a "Load register map" import), then atomically REPLACE the set via
  // tags:replace — which re-pushes the config so the gateway re-polls in a cycle.
  const [editing, setEditing] = useState<{ id: string; name: string; rows: TagRow[] } | null>(null);
  const [loadingTagsFor, setLoadingTagsFor] = useState<string | null>(null);
  const [savingTags, setSavingTags] = useState(false);
  const [importedMap, setImportedMap] = useState<RegisterMap | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const openEditor = async (it: { id: string; name: string }) => {
    setForm(null);
    setV2Form(false);
    setLinkEdit(null);
    setLoadingTagsFor(it.id);
    try {
      // Every tag, not the server's first page of 50: the editor's save replaces the whole set.
      const rows = (await things.allTags(it.id)).map(thingTagToRow);
      setEditing({
        id: it.id,
        name: it.name,
        rows: rows.length > 0 ? rows : [blankRow(EDIT_POLL_DEFAULT)],
      });
      setImportedMap(null);
      setImportErr(null);
    } catch (e) {
      toast.push("error", backendErrorMessage(e));
    } finally {
      setLoadingTagsFor(null);
    }
  };

  const editAddRow = () =>
    setEditing((s) => (s ? { ...s, rows: [...s.rows, blankRow(EDIT_POLL_DEFAULT)] } : s));
  const editRemoveRow = (key: string) =>
    setEditing((s) =>
      s ? { ...s, rows: s.rows.length > 1 ? s.rows.filter((r) => r.key !== key) : s.rows } : s,
    );
  const editPatchRow = (key: string, patch: Partial<TagRow>) =>
    setEditing((s) =>
      s ? { ...s, rows: s.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) } : s,
    );

  const applyMapUnit = (map: RegisterMap, unitId: string) => {
    const rows = registerMapToRows(map, unitId);
    if (rows.length === 0) {
      toast.push("info", `No registers for Modbus unit ${unitId} in that map.`);
      return;
    }
    setEditing((s) => (s ? { ...s, rows } : s));
    setImportedMap(null);
    toast.push("ok", `Loaded ${rows.length} registers (unit ${unitId}) from the map.`);
  };

  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    try {
      const map = JSON.parse(await f.text()) as RegisterMap;
      const units = map?.units ? Object.keys(map.units) : [];
      if (units.length === 0) {
        setImportErr('Not a register_map.json (no "units").');
        return;
      }
      setImportErr(null);
      if (units.length === 1) applyMapUnit(map, units[0]);
      else setImportedMap(map); // several units → show the unit picker
    } catch {
      setImportErr("Could not parse that file as JSON.");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const named = namedRowsOf(editing.rows);
    if (named.length === 0) {
      toast.push("error", "Name at least one tag before saving.");
      return;
    }
    setSavingTags(true);
    try {
      await tagsApi.replace(editing.id, { tags: toTagInputs(named) });
      toast.push("ok", `Tags updated — config pushed to ${editing.name} (${named.length} tags).`);
      setEditing(null);
      void invalidate();
      void qc.invalidateQueries({ queryKey: ["thing-tags", editing.id] });
      // The drawer's paged points and the reports' whole sets read again too.
      void qc.invalidateQueries({ queryKey: ["instrument-tags", editing.id] });
      void qc.invalidateQueries({ queryKey: ["thing-tags-all", editing.id] });
      void qc.invalidateQueries({ queryKey: ["spc-thing-tags", editing.id] });
    } catch (e) {
      toast.push("error", backendErrorMessage(e));
    } finally {
      setSavingTags(false);
    }
  };

  const editTagNameOptions = useMemo(
    () => Array.from(new Set((editing?.rows ?? []).map((r) => r.name.trim()).filter(Boolean))),
    [editing],
  );
  const editUnitOptions = useMemo(
    () => Array.from(new Set((editing?.rows ?? []).map((r) => r.unit.trim()).filter(Boolean))),
    [editing],
  );

  const patchForm = (patch: Partial<FormState>) =>
    setForm((f) => (f ? { ...f, ...patch } : f));

  const applyTemplate = (t: EquipmentTemplate) =>
    setForm((f) => ({
      ...(f ?? emptyForm(defaultProtocol)),
      name: f?.name || t.display_name.split(" — ")[0],
      equipmentType: t.equipment_type,
      protocol: t.protocol,
      slaveId: t.slave_id || 1,
      pollSec: t.default_poll_sec || 30,
      params: t.params.map((p) => ({
        metric: p.metric,
        register: p.register,
        reg_count: p.reg_count ?? 1,
        data_type: (p.data_type as ModbusType) ?? "float32",
        is_cycle_counter: Boolean(p.is_cycle_counter),
      })),
    }));

  const canSubmit = useMemo(
    () =>
      !!form &&
      form.name.trim().length > 0 &&
      form.params.some((p) => p.metric.trim() && p.register.trim()) &&
      // A TCP instrument needs an IP (the backend 400s without one) and a valid port.
      (form.protocol === "rtu" || (form.ip.trim().length > 0 && isPort(form.port))),
    [form],
  );

  const submit = () => {
    if (!form) return;
    // Link: only the chosen transport's fields are sent. The RTU line settings go
    // as shown (the form starts on the 9600 8N1 defaults); a blank serial port is
    // omitted, since only a PC / Pi gateway needs one.
    const link: Pick<
      CreateInstrumentBody,
      "ip" | "port" | "serial_port" | "baud" | "data_bits" | "parity" | "stop_bits"
    > =
      form.protocol === "tcp"
        ? { ip: form.ip.trim(), port: form.port }
        : {
            serial_port: form.serialPort.trim() || undefined,
            baud: form.baud,
            data_bits: form.dataBits,
            parity: form.parity,
            stop_bits: form.stopBits,
          };
    const body: CreateInstrumentBody = {
      name: form.name.trim(),
      equipment_type: form.equipmentType || undefined,
      protocol: form.protocol,
      slave_id: form.slaveId || undefined,
      ...link,
      poll_sec: form.pollSec || undefined,
      params: form.params
        .filter((p) => p.metric.trim() && p.register.trim())
        .map((p) => ({
          metric: p.metric.trim(),
          register: p.register.trim(),
          reg_count: regCountFor(p),
          data_type: p.data_type,
          is_cycle_counter: p.is_cycle_counter,
          // Only a type wider than one register has a word order; "big" (ABCD) is
          // the backend default, so it is omitted rather than sent.
          word_order:
            TYPE_REGISTERS[p.data_type] > 1 && p.word_order && p.word_order !== "big"
              ? p.word_order
              : undefined,
        })),
    };
    createM.mutate(body);
  };

  const patchParam = (i: number, patch: Partial<ParamRow>) =>
    setForm((f) =>
      f ? { ...f, params: f.params.map((p, j) => (j === i ? { ...p, ...patch } : p)) } : f,
    );

  return (
    <div className="flex flex-col gap-3">
      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return;
          try {
            await deleteM.mutateAsync(confirmRemove.id);
            setConfirmRemove(null);
          } catch {
            /* the mutation's onError has toasted; keep the dialog open to retry */
          }
        }}
        title={`Remove ${confirmRemove?.name ?? "this instrument"}?`}
        description="The gateway stops polling it, and its tags are removed. This can't be undone."
        confirmLabel="Remove instrument"
        destructive
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-on-surface-muted">
          <Cpu className="size-3.5" /> Instruments
          {instruments.length > 0 && (
            <span className="rounded bg-surface px-1.5 text-on-surface-muted">{instruments.length}</span>
          )}
        </div>
        {!form && !editing && !v2Form && !linkEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => (v2Gateway && v2Drivers.length > 0 ? setV2Form(true) : setForm(emptyForm(defaultProtocol)))}
          >
            <Plus className="mr-1 size-3.5" /> Add instrument
          </Button>
        )}
      </div>

      {/* existing instruments */}
      {instrumentsQ.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-on-surface-muted">
          <Loader2 className="size-3.5 animate-spin" /> Loading instruments…
        </div>
      ) : instruments.length === 0 && !form && !v2Form ? (
        <p className="text-xs text-on-surface-muted">
          {v2Gateway
            ? "No instruments yet. Add one with its protocol, connection and points, or find devices with Connect a device."
            : "No instruments yet. Add one from a VSync model template — its Modbus register map prefills."}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {instruments.slice(0, shownCount).map((it) => (
            <li
              key={it.id}
              className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-elevated px-2.5 py-1.5"
            >
              <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 truncate text-sm text-on-surface">
                  {it.name}
                  {it.driver && (
                    <Badge variant="secondary" className="text-[9px]">
                      {driverLabel(it.driver, allDrivers)}
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-on-surface-muted">
                  {it.tag_count ?? 0} params · {instrumentDataAge(it.last_seen, now)} · {it.slug}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      ref={(el) => {
                        if (el) valuesButtons.current.set(it.id, el);
                        else valuesButtons.current.delete(it.id);
                      }}
                      size="sm"
                      variant="ghost"
                      className={cn("h-7 gap-1 px-2 text-xs", valuesFor.has(it.id) && "bg-surface-muted text-on-surface")}
                      aria-pressed={valuesFor.has(it.id)}
                      onClick={() => toggleValues(it.id)}
                      aria-label={`Live values for ${it.name}`}
                    >
                      <Activity className="size-3.5" />
                      Values
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {valuesFor.has(it.id) ? "Hide the live values" : "Show the live values, read every 5 s"}
                  </TooltipContent>
                </Tooltip>
                {v2Gateway && it.driver && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={loadingLinkFor === it.id}
                    onClick={() => void openLinkEditor(it.id)}
                    aria-label={`Edit connection for ${it.name}`}
                    title="Edit this instrument's connection"
                  >
                    {loadingLinkFor === it.id ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Plug className="size-3.5" />
                    )}
                  </Button>
                )}
                {(!it.driver || isModbusDriver(it.driver)) && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={loadingTagsFor === it.id}
                  onClick={() => void openEditor(it)}
                  aria-label={`Edit tags for ${it.name}`}
                  title="Edit this instrument's tags"
                >
                  {loadingTagsFor === it.id ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Pencil className="size-3.5" />
                  )}
                </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger hover:bg-danger/10"
                  disabled={deleteM.isPending}
                  onClick={() => setConfirmRemove({ id: it.id, name: it.name })}
                  aria-label={`Remove ${it.name}`}
                  title="Remove this instrument"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
              </div>
              {valuesFor.has(it.id) && <InstrumentValues instrumentId={it.id} name={it.name} />}
            </li>
          ))}
          {instruments.length > shownCount && (
            <li className="flex flex-wrap items-center gap-2 px-1 pt-1 text-[11px] text-on-surface-muted">
              Showing {shownCount} of {instruments.length} instruments.
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-[11px]"
                onClick={() => showMore(shownCount + INSTRUMENTS_STEP)}
              >
                Show {Math.min(INSTRUMENTS_STEP, instruments.length - shownCount)} more
              </Button>
              {instruments.length - shownCount > INSTRUMENTS_STEP && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-[11px]"
                  onClick={() => showMore(instruments.length)}
                >
                  Show all
                </Button>
              )}
            </li>
          )}
        </ul>
      )}

      {waitingForBirth && !form && !v2Form && (
        <p className="text-[11px] text-on-surface-muted">
          Other protocols (OPC UA, S7, EtherNet/IP, MELSEC) can be added once the gateway is online on
          GatewayPro v0.3 or later and has announced them. Until then, instruments are Modbus.
        </p>
      )}

      {/* multi-protocol: edit a v2 instrument's connection (PATCH) */}
      {linkEdit && (
        <InstrumentLinkEditor
          gatewayId={gatewayId}
          instrument={linkEdit}
          driver={allDrivers?.find((d) => d.id === (linkEdit.driver ?? linkEdit.link?.driver))}
          serialPorts={serialPorts}
          onCancel={() => setLinkEdit(null)}
          onSaved={(res) => {
            toast.push(
              "ok",
              res.config_pushed
                ? `Connection saved — config pushed to the gateway.`
                : "Connection saved. It applies on the gateway's next config pull.",
            );
            setLinkEdit(null);
            void invalidate();
            void qc.invalidateQueries({ queryKey: ["thing", res.instrument_id] });
            // A poll interval typed there sets every point's, which the live values list shows.
            void qc.invalidateQueries({ queryKey: ["instrument-tags", res.instrument_id] });
          }}
        />
      )}

      {/* multi-protocol: add an instrument with the v2 body */}
      {v2Form && !editing && v2Drivers.length > 0 && (
        <InstrumentFormV2
          gatewayId={gatewayId}
          drivers={v2Drivers}
          serialPorts={serialPorts}
          templates={templates}
          onCancel={() => setV2Form(false)}
          onCreated={(res) => {
            const warn = res.warnings?.includes("gateway_not_announced")
              ? " The gateway has not announced itself yet, so it starts polling once it does."
              : "";
            toast.push(
              "ok",
              (res.config_pushed
                ? `Instrument added — config pushed to the gateway (${res.params} points).`
                : `Instrument added (${res.params} points). It applies on the gateway's next config pull.`) + warn,
            );
            setV2Form(false);
            void invalidate();
          }}
        />
      )}

      {/* edit-tags editor (#1 / #2) */}
      {editing && (
        <div className="flex flex-col gap-3 rounded-md border border-brand/40 bg-surface-elevated p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-on-surface">Edit tags — {editing.name}</span>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} aria-label="Cancel">
              <X className="size-3.5" />
            </Button>
          </div>

          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] text-on-surface-muted">
              Add, rename, retype or remove tags. Each row keeps its register address, space
              (coil/holding) and scale.
            </p>
            <div className="shrink-0 text-right">
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={onImportFile}
              />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="mr-1 size-3.5" /> Load register map
              </Button>
              {importErr && <p className="mt-1 text-[11px] text-danger">{importErr}</p>}
            </div>
          </div>

          {importedMap && (
            <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
              That map has several Modbus units — pick this instrument&apos;s unit:
              <select
                aria-label="Register-map unit"
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface"
                defaultValue=""
                onChange={(e) => e.target.value && applyMapUnit(importedMap, e.target.value)}
              >
                <option value="" disabled>
                  Select unit…
                </option>
                {Object.entries(importedMap.units).map(([uid, u]) => (
                  <option key={uid} value={uid}>
                    unit {uid} — {u.equipment_key}
                  </option>
                ))}
              </select>
            </label>
          )}

          <TagRowsEditor
            rows={editing.rows}
            onAddRow={editAddRow}
            onRemoveRow={editRemoveRow}
            onPatchRow={editPatchRow}
            thingSimulate={false}
            tagNameOptions={editTagNameOptions}
            unitOptions={editUnitOptions}
            showModbus
          />

          <p className="rounded bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400">
            Saving replaces this instrument&apos;s whole tag set and re-pushes its config. Existing
            equipment bindings to these tags are cleared — re-bind on the equipment page afterward.
          </p>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => void saveEdit()} disabled={savingTags}>
              {savingTags ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1 size-3.5" />
              )}
              Save tags
            </Button>
          </div>
        </div>
      )}

      {/* add form */}
      {form && !editing && (
        <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-elevated p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-on-surface">New instrument</span>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)} aria-label="Cancel">
              <X className="size-3.5" />
            </Button>
          </div>

          {/* template picker */}
          <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
            Model template (prefills the register map — verify per unit)
            <select
              aria-label="Equipment template"
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface"
              value={form.equipmentType}
              onChange={(e) => {
                const t = templates.find((x) => x.equipment_type === e.target.value);
                if (t) applyTemplate(t);
                else setForm((f) => (f ? { ...f, equipmentType: "" } : f));
              }}
            >
              <option value="">Custom (blank)…</option>
              {templates.map((t) => (
                <option key={t.equipment_type} value={t.equipment_type}>
                  {t.display_name} ({t.machines} in fleet)
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => patchForm({ name: e.target.value })}
                placeholder="e.g. Sand Strength 1"
              />
            </Field>
            <Field label="Poll interval (s)">
              <Input
                type="number"
                value={form.pollSec}
                onChange={(e) => patchForm({ pollSec: Number(e.target.value) })}
              />
            </Field>
          </div>

          <LinkFields form={form} onPatch={patchForm} />

          {/* register rows */}
          <div className="flex flex-col gap-1.5">
            <div className="text-[11px] font-medium text-on-surface-muted">Registers</div>
            {form.params.map((p, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Input
                    className="flex-1"
                    value={p.metric}
                    onChange={(e) => patchParam(i, { metric: e.target.value })}
                    placeholder="metric"
                    aria-label={`Param ${i + 1} metric`}
                  />
                  <Input
                    className="w-16"
                    value={p.register}
                    onChange={(e) => patchParam(i, { register: e.target.value })}
                    placeholder="reg"
                    aria-label={`Param ${i + 1} register`}
                  />
                  <select
                    aria-label={`Param ${i + 1} data type`}
                    className="w-24 rounded-md border border-border bg-surface px-1.5 py-1.5 text-xs text-on-surface"
                    value={p.data_type}
                    onChange={(e) => patchParam(i, { data_type: e.target.value as ModbusType })}
                  >
                    {MODBUS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <label
                    className="flex items-center gap-1 text-[10px] text-on-surface-muted"
                    title="Cycle counter (the poll trigger)"
                  >
                    <input
                      type="checkbox"
                      checked={p.is_cycle_counter}
                      onChange={(e) => patchParam(i, { is_cycle_counter: e.target.checked })}
                      aria-label={`Param ${i + 1} is cycle counter`}
                    />
                    cyc
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger hover:bg-danger/10"
                    onClick={() =>
                      setForm((f) =>
                        f ? { ...f, params: f.params.filter((_, j) => j !== i) } : f,
                      )
                    }
                    aria-label={`Remove param ${i + 1}`}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
                {/* Word order only exists for a type spanning 2+ registers. */}
                {TYPE_REGISTERS[p.data_type] > 1 && (
                  <div className="flex items-center justify-end gap-1.5 text-[10px] text-on-surface-muted">
                    <span aria-hidden="true">Word order</span>
                    <WordOrderSelect
                      value={p.word_order}
                      onChange={(w) => patchParam(i, { word_order: w })}
                      ariaLabel={`Param ${i + 1} word order`}
                      className="w-40 rounded-md border border-border bg-surface px-1.5 py-1 text-xs text-on-surface"
                    />
                  </div>
                )}
              </div>
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => setForm((f) => (f ? { ...f, params: [...f.params, blankParam()] } : f))}
            >
              <Plus className="mr-1 size-3" /> Add register
            </Button>
          </div>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={!canSubmit || createM.isPending}>
              {createM.isPending ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1 size-3.5" />
              )}
              Add instrument
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Label text above its control; the label wraps the control, so clicking the text
 *  focuses it. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
      <span>{label}</span>
      {children}
    </label>
  );
}

/**
 * The instrument's Modbus link: the transport, the slave / unit ID, then the
 * transport's own fields (TCP: IP + port; RTU: serial port + line settings).
 */
function LinkFields({
  form,
  onPatch,
}: {
  form: FormState;
  onPatch: (patch: Partial<FormState>) => void;
}) {
  const serialHintId = useId();
  const tcp = form.protocol === "tcp";
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
          <span>Transport</span>
          <div
            role="group"
            aria-label="Transport"
            className="flex h-10 overflow-hidden rounded-md border border-border bg-surface"
          >
            {TRANSPORTS.map((t) => {
              const active = form.protocol === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onPatch({ protocol: t.value })}
                  className={cn(
                    "flex-1 px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    active
                      ? "bg-brand text-brand-foreground"
                      : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                  )}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <Field label="Slave / unit ID">
          <Input
            type="number"
            value={form.slaveId}
            onChange={(e) => onPatch({ slaveId: Number(e.target.value) })}
          />
        </Field>

        {tcp ? (
          <>
            <Field label="IP address (required)">
              <Input
                required
                value={form.ip}
                onChange={(e) => onPatch({ ip: e.target.value })}
                placeholder="192.168.1.50"
              />
            </Field>
            <Field label="Port">
              <Input
                type="number"
                min={1}
                max={65535}
                value={form.port}
                onChange={(e) => onPatch({ port: Number(e.target.value) })}
                placeholder="502"
              />
            </Field>
          </>
        ) : (
          <div className="col-span-2 flex flex-col gap-1">
            <Field label="Serial port">
              <Input
                value={form.serialPort}
                onChange={(e) => onPatch({ serialPort: e.target.value })}
                placeholder="COM3 or /dev/ttyUSB0"
                aria-describedby={serialHintId}
              />
            </Field>
            <p id={serialHintId} className="text-[10px] text-on-surface-muted">
              A PC or Pi gateway needs this. VSync boxes ignore it.
            </p>
          </div>
        )}
      </div>

      {tcp && !isPort(form.port) && (
        <p className="text-[11px] text-danger">Port must be a whole number from 1 to 65535.</p>
      )}

      {!tcp && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Field label="Baud">
            <select
              className={SELECT_CLASS}
              value={form.baud}
              onChange={(e) => onPatch({ baud: Number(e.target.value) })}
            >
              {BAUD_RATES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Parity">
            <select
              className={SELECT_CLASS}
              value={form.parity}
              onChange={(e) =>
                onPatch({ parity: PARITIES.find((p) => p === e.target.value) ?? "N" })
              }
            >
              {PARITIES.map((p) => (
                <option key={p} value={p}>
                  {PARITY_LABEL[p]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Data bits">
            <select
              className={SELECT_CLASS}
              value={form.dataBits}
              onChange={(e) => onPatch({ dataBits: e.target.value === "7" ? 7 : 8 })}
            >
              <option value={8}>8</option>
              <option value={7}>7</option>
            </select>
          </Field>
          <Field label="Stop bits">
            <select
              className={SELECT_CLASS}
              value={form.stopBits}
              onChange={(e) => onPatch({ stopBits: e.target.value === "2" ? 2 : 1 })}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

/** When an instrument's readings last arrived (its last_seen, moved at most once a minute): "data 4 min ago". */
function instrumentDataAge(lastSeen: string | null | undefined, now: number): string {
  const age = readingAge(lastSeen, now);
  return age ? `data ${age}` : "no data yet";
}
